import { createError } from "#imports";
import type {
  PageType,
  StrapiEntity,
  StrapiAttributesPage,
  StrapiResponse,
} from "@/types";

import getAllProductsQuery from "@/graphql/strapi/getAllProducts.query.gql";
import getProductBySlugQuery from "@/graphql/strapi/getProductBySlug.query.gql";
import getNotificationsQuery from "@/graphql/strapi/getNotifications.query.gql";
import GetFAQsDocument from "@/graphql/strapi/getFAQs.query.gql";
import getExtraPacksQuery from "@/graphql/strapi/getExtraPacks.query.gql";
import getExtraPacksMoreQuery from "@/graphql/strapi/getExtraPacksMore.query.gql";
import getGlobalSettingsQuery from "@/graphql/strapi/getGlobalSettings.query.gql";
import getPageBySlugQuery from "@/graphql/strapi/getPageBySlug.query.gql";

import type {
  ProductType,
  GlobalSettingsType,
  StrapiProductType,
  NotificationType,
  ExtraPackType,
  ExtraPackMoreType,
  FAQType,
} from "@/types";

import { useStrapiUtils } from "@/composables/useStrapiUtils";
import { executeGraphQLQuery } from "@/utils/graphqlCache";

export const useStrapiService = () => {
  const { find, findOne } = useStrapi();
  const { pageFieldsToPopulate } = useStrapiUtils();

  const getPageBySlug = async (slug: string): Promise<any> => {
    const route = useRoute();

    const isPreview = route.query.preview === "true";

    const params: any = {
      filters: {
        slug: {
          $eq: slug,
        },
      },
      ...pageFieldsToPopulate,
    };

    if (isPreview) {
      params.status = "draft" as const;
    }
    // Security: Error handling without logging for security (2025-05-05)
    try {
      const response = await find<any>("pages", params);
      // Security: Error handling without logging for security (2025-05-05)
      if (!response?.data?.length) {
        // Security: Error handling without logging for security (2025-05-05)
        throw createError({
          statusCode: 404,
          statusMessage: "Page not found.",
          fatal: true,
        });
      }

      return response.data[0];
    } catch (err) {
      throw createError({
        statusCode: 500,
        statusMessage: err instanceof Error ? err.message : "Unknown error",
        fatal: true,
      });
    }
  };
  const getPageBySlugGraphql = async (slug: string) => {
    // Security: Error handling without logging for security (2025-05-05)
    try {
      // Extract the query string from the imported GraphQL document
      if (!getPageBySlugQuery.loc?.source.body) {
        console.error("Error: Could not extract GraphQL query");
        throw createError({
          statusCode: 500,
          statusMessage: "Could not extract GraphQL query",
          fatal: true,
        });
      }

      const queryString = getPageBySlugQuery.loc.source.body;

      // Execute with proper caching (15 minute TTL for pages)
      const result = await executeGraphQLQuery<any>({
        query: queryString,
        variables: { slug },
        operationName: 'GetPageBySlug'
      }, 15 * 60 * 1000);

      if (!result?.pages || result.pages.length === 0) {
        console.error("No pages found");
        throw createError({
          statusCode: 404,
          statusMessage: "Page not found",
          fatal: true,
        });
      }

      return result.pages[0];
    } catch (error) {
      console.error("Error in getPageBySlugGraphql:", error);
      throw createError({
        statusCode: 500,
        statusMessage: error instanceof Error ? error.message : "Unknown error",
        fatal: true,
      });
    }
  };

  const getStrapiProducts = async (): Promise<StrapiProductType[] | null> => {
    if (!getAllProductsQuery) {
      console.error("Error: ProductsQuery document is not available");
      return null;
    }

    // Extract the query string from the imported GraphQL document
    if (!getAllProductsQuery.loc?.source.body) {
      console.error("Error: Could not extract GraphQL query from document");
      return null;
    }

    const queryString = getAllProductsQuery.loc.source.body;

    // Execute with proper caching (30 minute TTL)
    // FIXED: Using correct operation name 'getAllProducts' to match the query definition
    const result = await executeGraphQLQuery<{
      products: StrapiProductType[];
    }>({
      query: queryString,
      operationName: 'getAllProducts'
    }, 30 * 60 * 1000);

    if (!result || !result.products) {
      console.error("No products found in response");
      return null;
    }

    return result.products || null;
  };

  const getProductBySlug = async (
    slug: string
  ): Promise<StrapiProductType | null> => {
    type responseType = {
      products: StrapiProductType[];
    };

    try {
      // Extract the query string from the imported GraphQL document
      if (!getProductBySlugQuery.loc?.source.body) {
        console.error("Error: Could not extract GraphQL query");
        return null;
      }

      const queryString = getProductBySlugQuery.loc.source.body;

      // Execute with proper caching (30 minute TTL)
      const result = await executeGraphQLQuery<responseType>({
        query: queryString,
        variables: { slug },
        operationName: 'GetProductBySlug'
      }, 30 * 60 * 1000);

      if (!result?.products || result.products.length === 0) {
        // Security: Error handling without logging for security (2025-05-05)
        return null;
      }

      // return the first product
      return result.products[0] || null;
    } catch (error) {
      console.error("Error in getProductBySlug:", error);
      return null;
    }
  };

  const getFaqsBySlug = async (slug: string) => {
    const { data: faq, error } = await useAsyncData(`faq:${slug}`, async () => {
      const params = {
        filters: {
          slug,
        },
        populate: ["faqs_category"],
      };

      const { data } = await findOne("faqs", params);

      return data;
    });

    if (error.value) {
      // Security: Error handling without logging for security (2025-05-05)
      return null;
    }

    if (!faq.value) {
      // Security: Error handling without logging for security (2025-05-05)

      throw createError({
        statusCode: 404,
        statusMessage: "FAQ not found.",
        fatal: true,
      });
    }

    return faq.value;
  };

  const getGlobalSettings = async (): Promise<GlobalSettingsType | null> => {
    try {
      // Extract the query string from the imported GraphQL document
      if (!getGlobalSettingsQuery.loc?.source.body) {
        console.error("Error: Could not extract GraphQL query");
        return null;
      }

      const queryString = getGlobalSettingsQuery.loc.source.body;

      // Using statically imported executeGraphQLQuery

      // Execute with proper caching (30 minute TTL)
      const result = await executeGraphQLQuery<{
        globalSetting: GlobalSettingsType;
      }>({
        query: queryString,
        operationName: 'GetGlobalSettings'
      }, 30 * 60 * 1000);

      if (!result) {
        // Security: Error handling without logging for security (2025-05-05)
        throw createError({
          statusCode: 404,
          statusMessage: "Global settings not found.",
          fatal: true,
        });
      }

      return result?.globalSetting || null;
    } catch (error) {
      console.error("Error in getGlobalSettings:", error);
      return null;
    }
  };

  const getNotifications = async (): Promise<NotificationType[] | null> => {
    try {
      type responseType = {
        notifications: NotificationType[];
      };

      // Extract the query string from the imported GraphQL document
      if (!getNotificationsQuery.loc?.source.body) {
        console.error("Error: Could not extract GraphQL query");
        return null;
      }

      const queryString = getNotificationsQuery.loc.source.body;
      
      // IMPORTANT: For date-based queries, we need to use a standardized date
      // to ensure consistent cache keys even as the current date changes
      // We'll use today's date with time set to 00:00:00 to get a stable cache key
      const now = new Date();
      // Format to YYYY-MM-DDT00:00:00.000Z for a stable date cache key
      const todayStandardized = now.toISOString().split('T')[0] + 'T00:00:00.000Z';
      
      // Use the standardized date for the cache key
      const variables = {
        currentDate: todayStandardized
      };

      // Using statically imported executeGraphQLQuery

      // Execute with proper caching (5 minute TTL for notifications)
      const result = await executeGraphQLQuery<responseType>({
        query: queryString,
        variables,
        operationName: 'GetNotifications'
      }, 5 * 60 * 1000);

      if (!result?.notifications) {
        console.error("No notifications found in response");
        return [];
      }

      return result.notifications;
    } catch (error) {
      console.error("Error in getNotifications:", error);
      return null;
    }
  };

  const getFAQs = async (): Promise<FAQType[] | null> => {
    try {
      type responseType = {
        faqs: FAQType[];
      };

      // Extract the query string from the imported GraphQL document
      if (!GetFAQsDocument.loc?.source.body) {
        console.error("Error: Could not extract GraphQL query");
        return null;
      }

      const queryString = GetFAQsDocument.loc.source.body;

      // Using statically imported executeGraphQLQuery

      // Execute with proper caching (60 minute TTL for FAQs)
      const result = await executeGraphQLQuery<responseType>({
        query: queryString,
        operationName: 'GetFAQs'
      }, 60 * 60 * 1000);
      return result?.faqs || null;
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      return null;
    }
  };

  const getExtraPacks = async (): Promise<ExtraPackType[] | null> => {
    type responseType = {
      extraPacks: ExtraPackType[];
    };

    try {
      // Extract the query string from the imported GraphQL document
      if (!getExtraPacksQuery.loc?.source.body) {
        console.error("Error: Could not extract GraphQL query");
        return null;
      }

      const queryString = getExtraPacksQuery.loc.source.body;

      // Using statically imported executeGraphQLQuery

      // Execute with proper caching (30 minute TTL for extra packs)
      const result = await executeGraphQLQuery<responseType>({
        query: queryString,
        operationName: 'GetExtraPacks'
      }, 30 * 60 * 1000);

      if (!result?.extraPacks) {
        console.error("No extra packs found");
        return null;
      }

      return result.extraPacks;
    } catch (error) {
      console.error("Error in getExtraPacks:", error);
      return null;
    }
  };

  const getExtraPacksMore = async (): Promise<ExtraPackMoreType[] | null> => {
    type responseType = {
      extraPackMores: ExtraPackMoreType[];
    };

    try {
      // Extract the query string from the imported GraphQL document
      if (!getExtraPacksMoreQuery.loc?.source.body) {
        console.error("Error: Could not extract GraphQL query");
        return null;
      }

      const queryString = getExtraPacksMoreQuery.loc.source.body;

      // Using statically imported executeGraphQLQuery

      // Execute with proper caching (30 minute TTL for extra packs more)
      const result = await executeGraphQLQuery<responseType>({
        query: queryString,
        operationName: 'GetExtraPacksMore'
      }, 30 * 60 * 1000);

      if (!result?.extraPackMores) {
        console.error("No extra packs more found");
        return null;
      }

      return result.extraPackMores;
    } catch (error) {
      console.error("Error in getExtraPacksMore:", error);
      return null;
    }
  };

  return {
    getPageBySlug,
    getPageBySlugGraphql,
    getStrapiProducts,
    getProductBySlug,
    getFaqsBySlug,
    getGlobalSettings,
    getNotifications,
    getFAQs,
    getExtraPacks,
    getExtraPacksMore,
  };
};
